import { IMAGE_ACCEPT, MEDIA_ACCEPT } from "../upload-constraints";
import type { FileUploadDefinition, UploadedFile } from "../types";

export { IMAGE_ACCEPT, MEDIA_ACCEPT } from "../upload-constraints";

export const DOCTOR_PHOTO_ACCEPT = IMAGE_ACCEPT;
export const DOCTOR_PHOTO_FIELD_ID_PREFIX = "doctorPhoto-";
export const PRODUCT_DETAIL_PHOTO_FIELD_ID_PREFIX = "productDetailPhoto-";
export const LEGACY_DOCTOR_PHOTO_FIELD_ID = "doctorStaffPhotos";
export const HERO_IMAGE_DETAIL_TARGET_RATIO = 1200 / 935;
export const HERO_IMAGE_LIST_CARD_VISIBLE_RATIO = 208 / 188;

const IMAGE_UPLOAD_LIMIT_BYTES = 10 * 1024 * 1024;
const VIDEO_UPLOAD_LIMIT_BYTES = 50 * 1024 * 1024;
const PDF_UPLOAD_LIMIT_BYTES = 20 * 1024 * 1024;

function fieldValueToString(value: unknown): string {
  if (typeof value === "string") return value;
  if (typeof value === "number" || typeof value === "boolean") return String(value);
  return "";
}

export function formatDateTime(value?: string): string {
  if (!value) return "아직 작성 전";
  try {
    return new Intl.DateTimeFormat("ko-KR", {
      month: "long",
      day: "numeric",
      hour: "numeric",
      minute: "2-digit",
    }).format(new Date(value));
  } catch {
    return value;
  }
}

export function formatBytes(size: number): string {
  if (size < 1024) return `${size}B`;
  if (size < 1024 * 1024) return `${Math.round(size / 102.4) / 10}KB`;
  return `${Math.round(size / 1024 / 102.4) / 10}MB`;
}

export function getUploadFileLimitBytes(file: File): number {
  if (file.type === "application/pdf") return PDF_UPLOAD_LIMIT_BYTES;
  if (file.type.startsWith("video/")) return VIDEO_UPLOAD_LIMIT_BYTES;
  return IMAGE_UPLOAD_LIMIT_BYTES;
}

export function uploadLimitDescription(accept?: string): string {
  const allowsVideo = accept?.includes("video/");
  const allowsPdf = accept?.includes("application/pdf");
  if (allowsVideo && allowsPdf) return "이미지 10MB / 영상 50MB / PDF 20MB 이하";
  if (allowsVideo) return "이미지 10MB / 영상 50MB 이하";
  return allowsPdf ? "이미지 10MB / PDF 20MB 이하" : "이미지 10MB 이하";
}

export function splitFileName(fileName: string): { baseName: string; extension: string } {
  const dotIndex = fileName.lastIndexOf(".");
  if (dotIndex <= 0) return { baseName: fileName, extension: "" };
  return { baseName: fileName.slice(0, dotIndex), extension: fileName.slice(dotIndex) };
}

export function sanitizeFileBaseName(baseName: string): string {
  return baseName.replace(/[\\/:*?"<>|]/g, "-").replace(/[\r\n]+/g, " ");
}

export function getDoctorPhotoFieldId(index: number): string {
  return `${DOCTOR_PHOTO_FIELD_ID_PREFIX}${index}`;
}

export function createDoctorPhotoUploadDefinition(index: number, doctor: unknown): FileUploadDefinition {
  const doctorRecord = doctor && typeof doctor === "object" ? doctor as Record<string, unknown> : {};
  const doctorName = fieldValueToString(doctorRecord.name).trim();
  return {
    id: getDoctorPhotoFieldId(index),
    category: "doctor_staff_photo",
    label: doctorName ? `${doctorName} 사진` : `의료진 / 전문가 ${index + 1} 사진`,
    description: "해당 의료진/전문가 소개에 사용할 대표 사진 1장을 첨부해주세요.",
    accept: DOCTOR_PHOTO_ACCEPT,
    maxFiles: 1,
  };
}

export function getProductDetailPhotoFieldId(index: number): string {
  return `${PRODUCT_DETAIL_PHOTO_FIELD_ID_PREFIX}${index}`;
}

export function createProductDetailPhotoUploadDefinition(index: number, product: unknown): FileUploadDefinition {
  const productRecord = product && typeof product === "object" ? product as Record<string, unknown> : {};
  const productName = fieldValueToString(productRecord.optionName).trim();
  return {
    id: getProductDetailPhotoFieldId(index),
    category: "product_detail_photo",
    label: productName ? `${productName} 관련 사진` : `시술 ${index + 1} 관련 사진`,
    description: "이 시술 설명에 함께 보여주면 좋은 시술 장면, 기기, 결과 예시 사진이 있다면 첨부해주세요.",
    accept: MEDIA_ACCEPT,
    multiple: true,
    maxFiles: 6,
  };
}

export function isUploadedFileForDefinition(file: UploadedFile, definition: FileUploadDefinition): boolean {
  if (file.category !== definition.category) return false;
  if (definition.id === getDoctorPhotoFieldId(0) && file.fieldId === LEGACY_DOCTOR_PHOTO_FIELD_ID) return true;
  if (!file.fieldId) {
    if (definition.id.startsWith(DOCTOR_PHOTO_FIELD_ID_PREFIX)) return definition.id === getDoctorPhotoFieldId(0);
    if (definition.id.startsWith(PRODUCT_DETAIL_PHOTO_FIELD_ID_PREFIX)) return definition.id === getProductDetailPhotoFieldId(0);
    return true;
  }
  return file.fieldId === definition.id;
}

export function reindexDoctorPhotoFilesAfterRemoval(files: UploadedFile[], removedIndex: number): UploadedFile[] {
  return files.flatMap((file) => {
    if (file.category !== "doctor_staff_photo" || !file.fieldId?.startsWith(DOCTOR_PHOTO_FIELD_ID_PREFIX)) return [file];
    const index = Number.parseInt(file.fieldId.slice(DOCTOR_PHOTO_FIELD_ID_PREFIX.length), 10);
    if (!Number.isFinite(index)) return [file];
    if (index === removedIndex) return [];
    if (index > removedIndex) return [{ ...file, fieldId: getDoctorPhotoFieldId(index - 1) }];
    return [file];
  });
}

export function reindexProductDetailPhotoFilesAfterRemoval(files: UploadedFile[], removedIndex: number): UploadedFile[] {
  return files.flatMap((file) => {
    if (file.category !== "product_detail_photo" || !file.fieldId?.startsWith(PRODUCT_DETAIL_PHOTO_FIELD_ID_PREFIX)) return [file];
    const index = Number.parseInt(file.fieldId.slice(PRODUCT_DETAIL_PHOTO_FIELD_ID_PREFIX.length), 10);
    if (!Number.isFinite(index)) return [file];
    if (index === removedIndex) return [];
    if (index > removedIndex) return [{ ...file, fieldId: getProductDetailPhotoFieldId(index - 1) }];
    return [file];
  });
}
